
import { Component, Vue, Prop, Inject, Watch } from 'vue-property-decorator';
import { generateUID } from '@/utils/utils';
import STable from './STable.vue';
import { NormalizedScopedSlot, ScopedSlotChildren, VNode } from 'vue/types/vnode';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { IColumnFilterOptions, filter } from './filtering';
import { IColumnSortOptions } from './sorting';
import { Debounce } from '@/decorators/debounce';
import TableFiltersService from '@/components/UI/Table/table-filters-service';

@Component
export default class SColumn<Item> extends Vue {
  @Prop(String) public header?: string;
  @Prop() private contentGetter?: string | ((item: Item) => string);
  @Prop() private title?: string | ((item: Item) => string) | boolean;

  public uid: string = generateUID();

  private created() {
    this.initFiltering();
    this.initSorting();
    this.sTable.reactiveProps.columns.push(this);
  }

  public get template(): NormalizedScopedSlot {
    return this.$scopedSlots.default || this.defaultTemplate;
  }

  public get class() {
    return this.$vnode.data!.class;
  }

  public get style() {
    return this.$vnode.data!.style;
  }

  public getTitle(item: Item): string {
    if (typeof this.title === 'undefined') return '';
    if (this.title === false) return '';
    const title = this.title === '' ? true : this.title;

    const value = typeof title === 'function'
      ? title(item)
      : typeof title === 'string'
        ? get(item, title || '')
        : this.getContent(item);
    return [ null, undefined ].includes(value) ? '' : value.toString();
  }

  @Inject({ from: 'sTable' }) private sTable!: STable<Item>;

  private destroyed() {
    this.sTable.reactiveProps.columns.splice(
      this.sTable.reactiveProps.columns.indexOf(this),
      1,
    );
  }

  private getContent(item: Item): string {
    const value = typeof this.contentGetter === 'function'
      ? this.contentGetter(item)
      : get(item, this.contentGetter || '');
    return [ null, undefined ].includes(value) ? '' : value.toString();
  }

  private defaultTemplate({ item }: { item: Item }): ScopedSlotChildren {
    const node: VNode = { text: this.getContent(item), isRootInsert: false, isComment: false };
    return [ node ];
  }

  private render() {
    return '';
  }

  // FILTERING

  @Prop(Object) private filterOptions?: IColumnFilterOptions<Item>;
  public filtering!: IColumnFilterOptions<Item>;
  public filter = '';

  private initFiltering() {
    this.filtering = merge(
      {
        type: 'input',
        initial: '',
        filterGetter: this.contentGetter,
        filterName: this.uid,
        filterWith: filter,
      },
      this.filterOptions,
    );

    this.filtering.enabled = Boolean(
      (this.sTable?.filterOptions?.enabled || false) && // table is filterable
      (this.filtering?.enabled !== false) && // column is filterable
      (
         // there is a way to filter by or the table is remote filtered
        this.filtering.filterGetter ||
        (this.sTable?.filterOptions?.remoteFiltering || false)
      ),
    );

    if (this.sTable.filtersInUrl) {
      const urlFilters = TableFiltersService.get(this.sTable.filtersInUrl);
      const filterValue = urlFilters && urlFilters[this.filterOptions?.filterName!];
      // if user empties the input with initial value it should remember it as empty filter
      this.filter = filterValue === '' ? '' : filterValue || this.filtering.initial!;
    } else {
      this.filter = this.filtering.initial!;
    }
  }

  @Watch('filter')
  @Debounce<SColumn<Item>>(function() { return this.filterDebounceTime })
  private filterChanged(): void {
    this.sTable.filterChanged();
    this.sTable.changeFiltersInUrl();
  }

  public filterPasses(item: Item): boolean {
    if (this.filter === '' || this.filter === null) return true;

    const value = typeof this.filtering.filterGetter === 'function'
      ? this.filtering.filterGetter!(item)
      : get(item, this.filtering.filterGetter!);

    return this.filtering.filterWith!(value, this.filter);
  }

  public resetFilter(): void {
    this.initFiltering();
  }

  private get filterDebounceTime(): number {
    return this.sTable?.filterOptions?.debounce || 0;
  }

  // SORTING

  @Prop(Object) private sortOptions?: IColumnSortOptions<Item>;
  public sorting!: IColumnSortOptions<Item>;

  private initSorting() {
    this.sorting = merge(
      {
        sortGetter: this.contentGetter,
        sortName: this.uid,
      },
      this.sortOptions);

    this.sorting.enabled = Boolean(
      (this.sTable?.sortOptions?.enabled || false) && // table is sortable
      (this.sorting?.enabled !== false) && // column is sortable
      this.sorting.sortGetter, // there is a way to sort by
    );
  }
}
